import { ZERO_QUANTITY, parseQuantity } from '@aeris/inventory'

/**
 * Quantas etiquetas o recebimento de uma compra pede (F124, entrada de compra).
 *
 * ## O que chegou AGORA, e nunca o que foi pedido
 *
 * Recebimento parcial é normal: pediram 10, chegaram 3. Etiquetar 10 seria
 * colar 7 adesivos em peças que não estão na loja — e o PDV leria de volta um
 * código de algo que ninguém recebeu. A quantidade é a que a pessoa digitou no
 * recebimento, que é a mesma que o estoque acabou de receber.
 *
 * ## Uma etiqueta por peça, e o que não se conta em peça
 *
 * `2,5 KG` de um produto a granel não são duas etiquetas e meia. Quantidade
 * fracionada vira UMA etiqueta — a da gôndola —, e quem precisar de mais ajusta
 * na fila. Arredondar para 3 ou para 2 seria inventar peças.
 */

export type ReceivedLine = {
  readonly variantId: string
  /** O que foi digitado no recebimento, como texto do balcão: `3`, `2,5`. */
  readonly typedQuantity: string
}

export type LabelDemand = {
  readonly variantId: string
  readonly labels: number
}

export const labelsFromReceipt = (
  lines: readonly ReceivedLine[]
): readonly LabelDemand[] => {
  const byVariant = new Map<string, number>()

  for (const line of lines) {
    const quantity =
      parseQuantity(line.typedQuantity.replace(',', '.')) ?? ZERO_QUANTITY
    if (quantity.units <= 0n) continue

    const scaleFactor = 10n ** BigInt(quantity.scale)
    const whole = quantity.units % scaleFactor === 0n
    const labels = whole ? Number(quantity.units / scaleFactor) : 1

    // A mesma variante em duas linhas da nota soma: quem cola não quer duas
    // linhas iguais na fila para somar de cabeça.
    byVariant.set(line.variantId, (byVariant.get(line.variantId) ?? 0) + labels)
  }

  return [...byVariant].map(([variantId, labels]) => ({ variantId, labels }))
}

/**
 * As etiquetas de uma compra REABERTA (F195): o que já chegou, somado.
 *
 * `receivedQuantity`, e nunca `quantity`: a compra parcial recebeu 3 de 10, e
 * os 7 que faltam não estão na loja para receber adesivo. A regra é a mesma do
 * recebimento — inteiro vira N etiquetas, fracionado vira uma.
 */
export const labelsFromPurchase = (
  lines: readonly {
    readonly variantId: string
    readonly receivedQuantity: string
  }[]
): readonly LabelDemand[] =>
  labelsFromReceipt(
    lines.map((line) => ({
      variantId: line.variantId,
      typedQuantity: line.receivedQuantity,
    }))
  )
